import { getRequestsByCommunityId } from '@/api/requests';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Container, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus';
import { ClipboardText as ClipboardIcon } from '@phosphor-icons/react/dist/ssr/ClipboardText';

import type { Request } from '@/types/v2/request';
import { useUser } from '@/hooks/v2/use-user';
import { useDialog } from '@/hooks/v2/use-dialog';
import { Seo } from '@/components/seo';

import { RequestsTable } from './components/requests-table';
import CreateRequestDialog from './request/components/create-request-dialog';

export default function RequestsPageV2() {
  const { community } = useUser();
  const dialog = useDialog();

  const { data, isLoading, error } = useQuery<{ requests: Request[] }>({
    queryKey: ['requests', community?.id],
    queryFn: async () => {
      return await getRequestsByCommunityId(community?.id, {
        params: {
          sort: '-created_at',
          include: 'requestType,requestStatus,unit',
        },
      });
    },
  });

  const requests = data?.requests || [];

  return (
    <>
      <Seo title="Requests" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Requests</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  View and manage maintenance, incident, and ARC requests
                </Typography>
              </Box>
              <Button
                onClick={dialog.handleOpen}
                size="small"
                startIcon={<PlusIcon size={16} />}
                variant="contained"
              >
                New Request
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {isLoading ? (
              <Stack>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton height={18} variant="text" width="55%" />
                        <Skeleton height={14} sx={{ mt: 0.5 }} variant="text" width="35%" />
                      </Box>
                      <Skeleton height={14} variant="text" width={80} />
                    </Stack>
                    {i < 5 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Stack>
            ) : error ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="error" variant="body2">
                  Unable to load requests. Please try again.
                </Typography>
              </Box>
            ) : requests.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <ClipboardIcon color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
                  No requests yet
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
                  Create a request to get started
                </Typography>
              </Box>
            ) : (
              <RequestsTable error={error} loading={isLoading} rows={requests} />
            )}
          </Paper>
        </Container>
      </Box>

      <CreateRequestDialog onClose={dialog.handleClose} open={dialog.open} />
    </>
  );
}
